import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDuckDB } from '../context/LazyDuckDBContext';
import { calculateCorrelation as calcCorrelation } from '../utils/statistics';
import { AnalyticalFilters } from '../types/filters';
import { buildAnalyticalCTEs, buildAnalyticalWhere, hasActiveAnalyticalFilters } from '../utils/analyticalFilters';

// Data interfaces for kommun view
export interface KommunKpiData {
  avgMeritvarde: number | null;
  avgSalsaAvvikelse: number | null;
  ranking: { rank: number; total: number } | null;
  spridning: number | null;
  reportedSchools: number;
  totalSchools: number;
  latestYear: number;
}

export interface StudentCountStatus {
  selectedYear: number;
  latestAvailableYear: number | null;
  missingForSelectedYear: boolean;
  carryForwardFromYear: number | null;
}

export interface KommunTrendData {
  year: number;
  meritvarde: number;
  salsa_forvantat: number | null;
  riksValue: number | null;
}

export interface SchoolDistributionData {
  ou_id: string;
  ou_name: string;
  value: number;
  diff_mot_kommunmedel: number;
  kommunMedel: number | null;
  color_class: 'over' | 'inline' | 'under';
}

export interface DriverData {
  year: number;
  salsa_avvikelse: number;
  lärarbehörighet: number | null;
  personaltäthet: number | null;
  elever_per_lärare: number | null;
}

export interface InsightData {
  latestAvvikelse: number;
  trendSlope: number;
  strongestFactor: string;
  factorCorrelation: number;
}

// New interfaces for the expanded data model
export interface AllKpiData {
  [kpiId: string]: Array<{ year: number; value: number; prevYearValue: number | null; riksValue: number | null }>;
}

export interface DynamicTrendData {
  year: number;
  kpiValue: number;
  salsa_forvantat: number | null;
  riksValue: number | null;
}

export interface DynamicDistributionData {
  ou_id: string;
  ou_name: string;
  value: number;
  kommunMedel: number;
  color_class: 'over' | 'inline' | 'under';
}

export interface GenderDistributionData {
  year: number;
  muni_girls: number | null;
  muni_boys: number | null;
}

export interface ProgressionData {
  cohort_start_year: number;
  np3_andel: number | null;
  e6_andel: number | null;
  e9_andel: number | null;
  tapp_3_6: number | null;
  tapp_6_9: number | null;
}

// structures for correlation analysis
export interface CorrelationPoint {
  ou_id: string;
  ou_name: string;
  kpiValue: number;
  driverValue: number;
  year: number;
}

export interface CorrelationYearSummary {
  year: number;
  r: number;
  n: number;
  avgDriver: number;
  avgKpi: number;
  minDriver: number;
  maxDriver: number;
  minKpi: number;
  maxKpi: number;
  points: CorrelationPoint[];
}

interface CachedDynamicPayload {
  trend: DynamicTrendData[];
  distribution: DynamicDistributionData[];
}

interface CachedCorrelationPayload {
  correlation: { [driver: string]: CorrelationYearSummary[] };
  riket: { [driver: string]: { year: number; r: number }[] };
}

// Helper to get property case-insensitively
const getProp = (obj: any, key: string) => {
  if (!obj) return undefined;
  if (obj[key] !== undefined) return obj[key];
  const lowerKey = key.toLowerCase();
  const foundKey = Object.keys(obj).find(k => k.toLowerCase() === lowerKey);
  return foundKey ? obj[foundKey] : undefined;
};

// Helper function to calculate correlation
function calculateCorrelation(x: number[], y: number[]): number {
  const n = x.length;
  if (n === 0) return 0;
  const sumX = x.reduce((a, b) => a + b, 0);
  const sumY = y.reduce((a, b) => a + b, 0);
  const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i], 0);
  const sumXX = x.reduce((sum, xi) => sum + xi * xi, 0);
  const sumYY = y.reduce((sum, yi) => sum + yi * yi, 0);

  const numerator = n * sumXY - sumX * sumY;
  const denominator = Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY));

  return denominator === 0 ? 0 : numerator / denominator;
}

export const useKommunData = (
  kommunId: string | null,
  year: number,
  activeKpi?: string,
  huvudmanId?: string | null,
  skolaId?: string | null,
  analyticalFilters?: AnalyticalFilters
) => {
  const { query, getCached, dataVersion } = useDuckDB();

  const analyticalFiltersKey = useMemo(
    () => JSON.stringify(analyticalFilters ?? {}),
    [analyticalFilters]
  );

  // 1. ALL HOOKS AT THE VERY TOP OF THE FUNCTION
  const [lastCorrelationKey, setLastCorrelationKey] = useState<string>('');

  const activeKpiRef = useRef<string>(activeKpi ?? '');
  activeKpiRef.current = activeKpi ?? '';
  const fetchDynamicDataRef = useRef<((kpiId: string) => Promise<void>) | null>(null);

  // LRU-like cache with max size to prevent unbounded memory growth
  const dynamicCacheRef = useRef<Map<string, CachedDynamicPayload>>(new Map());
  const correlationCacheRef = useRef<Map<string, CachedCorrelationPayload>>(new Map());
  const abortControllerRef = useRef<AbortController | null>(null);
  const correlationDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const [allKpiData, setAllKpiData] = useState<AllKpiData>(() => {
    if (!kommunId) return {};

    const kpiIds = ['N15504', 'N15501', 'N15418', 'N15436', 'N15494', 'N15502', 'N15548', 'N15497', 'N15482', 'N15488', 'N15516', 'N15485', 'N15033', 'N15809', 'N15813', 'U15416'];
    const kpiList = kpiIds.map(id => `'${id}'`).join(', ');
    const startYear = year - 6;
    const endYear = year;
    const schoolId = (skolaId && skolaId !== 'null' ? skolaId : null);

    // Use gold_kpi_muni_year whenever possible (always local)
    // Only use gold_kpi_trend (hybrid local/S3) when we need school-level or governance data
    const useMuniTable = !huvudmanId && !schoolId;

    const q1 = `
          WITH base AS (
            SELECT
              kpi_id,
              year,
              ${useMuniTable ? 'AVG(value_t)' : `
                CASE
                  WHEN SUM(CASE WHEN students > 0 THEN students ELSE 0 END) > 0
                    THEN SUM(value * students) / NULLIF(SUM(students), 0)
                  ELSE AVG(value)
                END`} AS value
            FROM ${useMuniTable ? 'gold_kpi_muni_year' : 'gold_kpi_trend'}
            WHERE municipality_id = :kommunId AND kpi_id IN (${kpiList})
              AND year BETWEEN :startYear AND :endYear
              ${useMuniTable ? 'AND value_t IS NOT NULL' : `
                AND entity_type = 'org_unit' AND gender = 'T'
                AND value IS NOT NULL
                ${huvudmanId ? 'AND governance_type_year = :governance' : ''}
                AND (:schoolId IS NULL OR entity_id = :schoolId)`}
            GROUP BY kpi_id, year
          )
          SELECT kpi_id, year, value, LAG(value) OVER (PARTITION BY kpi_id ORDER BY year) AS prev_year_value
          FROM base ORDER BY kpi_id, year
        `;
    const p1 = { kommunId, startYear, endYear, ...(huvudmanId ? { governance: huvudmanId } : {}), schoolId };

    const q2 = `
          SELECT kpi_id, year, AVG(value_t) AS value
          FROM gold_kpi_muni_year
          WHERE kpi_id IN (${kpiList})
            AND year BETWEEN :startYear AND :endYear
            AND value_t IS NOT NULL
          GROUP BY kpi_id, year
        `;
    const p2 = { kommunId, startYear, endYear };

    const cached1 = getCached(q1, p1);
    const cached2 = getCached(q2, p2);

    if (cached1 && cached2) {
      const processed: AllKpiData = {};
      for (const row of (cached1 as any[])) {
        const kId = (row as any).kpi_id;
        const yVal = Number((row as any).year);
        const curVal = (row as any).value != null ? Number((row as any).value) : null;
        const prevVal = (row as any).prev_year_value != null ? Number((row as any).prev_year_value) : null;
        if (curVal == null || !Number.isFinite(curVal) || Number.isNaN(yVal)) continue;
        if (!processed[kId]) processed[kId] = [];
        const riksRow = (cached2 as any[]).find(r => (r as any).kpi_id === kId && Number((r as any).year) === yVal);
        const rVal = riksRow ? Number((riksRow as any).value) : null;
        processed[kId].push({ year: yVal, value: curVal, prevYearValue: prevVal, riksValue: rVal });
      }
      return processed;
    }
    return {};
  });

  const [loading, setLoading] = useState(() => {
    if (!kommunId) return false;
    return Object.keys(allKpiData).length === 0;
  });

  const [kpiData, setKpiData] = useState<KommunKpiData | null>(() => {
    if (!kommunId || Object.keys(allKpiData).length === 0) return null;
    const meritSeries = allKpiData['N15504'] ?? [];
    if (meritSeries.length === 0) return null;
    const latestYearFound = Math.max(...meritSeries.map(s => s.year));
    const latestMerit = meritSeries.find(s => s.year === latestYearFound)?.value ?? null;
    const latestSalsa = allKpiData['U15416']?.find(s => s.year === latestYearFound)?.value ?? null;
    return {
      avgMeritvarde: latestMerit,
      avgSalsaAvvikelse: latestSalsa,
      ranking: { rank: 1, total: 1 },
      spridning: null,
      reportedSchools: 0,
      totalSchools: 0,
      latestYear: latestYearFound,
    };
  });

  const [trendData, setTrendData] = useState<KommunTrendData[]>(() => {
    if (!kommunId || Object.keys(allKpiData).length === 0) return [];
    const meritSeries = allKpiData['N15504'] ?? [];
    const salsaSeries = allKpiData['U15416'] ?? [];
    const salsaByYear = new Map(salsaSeries.map(item => [item.year, item.value]));
    return meritSeries.map(item => ({
      year: item.year,
      meritvarde: item.value,
      salsa_forvantat: salsaByYear.has(item.year) ? Number((item.value ?? 0) - (salsaByYear.get(item.year) ?? 0)) : null,
      riksValue: item.riksValue,
    }));
  });

  const [distributionData, setDistributionData] = useState<SchoolDistributionData[]>(() => {
    if (!kommunId) return [];
    const latestYearToUse = year;
    const cached = getCached(`
          WITH KommunMedel AS (
            SELECT AVG(value_t) AS mu
            FROM gold_kpi_muni_year
            WHERE municipality_id = :kommunId
              AND kpi_id = 'N15504'
              AND year = :latestYear
              AND value_t IS NOT NULL
          ),
          SchoolValues AS (
            SELECT t.entity_id AS ou_id, t.entity_name AS ou_name, AVG(t.value) AS value
            FROM gold_kpi_trend t
            WHERE t.municipality_id = :kommunId AND t.kpi_id = 'N15504' AND t.gender = 'T'
              AND t.entity_type = 'org_unit' AND t.year = :latestYear
              AND t.value IS NOT NULL
            GROUP BY t.entity_id, t.entity_name
          ),
          Params AS (SELECT mu, 3.0 AS tol FROM KommunMedel)
          SELECT s.ou_id, s.ou_name, s.value, s.value - p.mu AS diff_mot_kommunmedel, p.mu AS kommunMedel,
            CASE WHEN s.value >= p.mu + p.tol THEN 'over' WHEN s.value <= p.mu - p.tol THEN 'under' ELSE 'inline' END AS color_class
          FROM SchoolValues s CROSS JOIN Params p ORDER BY s.value DESC
        `, { kommunId, latestYear: latestYearToUse });
    return (cached as SchoolDistributionData[]) || [];
  });

  const [driverData, setDriverData] = useState<DriverData[]>([]);
  const [insightData, setInsightData] = useState<InsightData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dynamicTrendData, setDynamicTrendData] = useState<DynamicTrendData[]>([]);
  const [dynamicDistributionData, setDynamicDistributionData] = useState<DynamicDistributionData[]>([]);
  const [genderData, setGenderData] = useState<GenderDistributionData[]>([]);
  const [progressionData, setProgressionData] = useState<ProgressionData[]>([]);
  const [correlationData, setCorrelationData] = useState<{ [driver: string]: CorrelationYearSummary[] }>({});
  const [riketData, setRiketData] = useState<{ [driver: string]: { year: number, r: number }[] }>({});
  const [studentCountStatus, setStudentCountStatus] = useState<StudentCountStatus | null>(null);

  const latestYearForDistributionRef = useRef(year);
  useEffect(() => {
    latestYearForDistributionRef.current = kpiData?.latestYear ?? year;
  }, [kpiData?.latestYear, year]);

  // Helper to process aggregated KPI data from rows
  const processKpiData = useCallback((aggregatedRows: any[], riksRows: any[]) => {
    const processed: AllKpiData = {};
    for (const row of aggregatedRows) {
      const kpiId = getProp(row, "kpi_id") as string;
      const yearValue = Number(getProp(row, "year"));
      const currentValue = getProp(row, "value") != null ? Number(getProp(row, "value")) : null;
      const prevValue = getProp(row, "prev_year_value") != null ? Number(getProp(row, "prev_year_value")) : null;

      if (currentValue == null || !Number.isFinite(currentValue) || Number.isNaN(yearValue)) continue;
      if (!processed[kpiId]) processed[kpiId] = [];

      const riksRow = riksRows.find(r => getProp(r, "kpi_id") === kpiId && Number(getProp(r, "year")) === yearValue);
      const riksValue = riksRow ? Number(getProp(riksRow, "value")) : null;

      processed[kpiId].push({
        year: yearValue,
        value: currentValue,
        prevYearValue: prevValue,
        riksValue: riksValue
      });
    }
    Object.values(processed).forEach(series => series.sort((a, b) => a.year - b.year));
    return processed;
  }, []);

  const addToCache = useCallback(<T,>(cache: Map<string, T>, key: string, value: T) => {
    const MAX_SIZE = 50;
    if (cache.size >= MAX_SIZE) {
      const firstKey = cache.keys().next().value;
      if (firstKey) cache.delete(firstKey);
    }
    cache.set(key, value);
  }, []);

  const fetchData = useCallback(async () => {
    if (!kommunId) {
      setLoading(false);
      return;
    }

    const startYear = year - 6;
    const endYear = year;
    const kpiIds = ['N15504', 'N15501', 'N15418', 'N15436', 'N15494', 'N15502', 'N15548', 'N15497', 'N15482', 'N15488', 'N15516', 'N15485', 'N15033', 'N15809', 'N15813', 'U15416'];
    const kpiList = kpiIds.map(id => `'${id}'`).join(', ');
    const schoolId = skolaId && skolaId !== 'null' ? skolaId : null;
    const useMuniTable = !huvudmanId && !schoolId;

    const summaryCache = getCached(`
          WITH base AS (
            SELECT
              kpi_id,
              year,
              ${useMuniTable ? 'AVG(value_t)' : `
                CASE
                  WHEN SUM(CASE WHEN students > 0 THEN students ELSE 0 END) > 0
                    THEN SUM(value * students) / NULLIF(SUM(students), 0)
                  ELSE AVG(value)
                END`} AS value
            FROM ${useMuniTable ? 'gold_kpi_muni_year' : 'gold_kpi_trend'}
            WHERE municipality_id = :kommunId AND kpi_id IN (${kpiList})
              AND year BETWEEN :startYear AND :endYear
              ${useMuniTable ? 'AND value_t IS NOT NULL' : `
                AND entity_type = 'org_unit' AND gender = 'T'
                AND value IS NOT NULL
                ${huvudmanId ? 'AND governance_type_year = :governance' : ''}
                AND (:schoolId IS NULL OR entity_id = :schoolId)`}
            GROUP BY kpi_id, year
          )
          SELECT kpi_id, year, value, LAG(value) OVER (PARTITION BY kpi_id ORDER BY year) AS prev_year_value
          FROM base ORDER BY kpi_id, year
        `, { kommunId, startYear, endYear, ...(huvudmanId ? { governance: huvudmanId } : {}), schoolId });

    if (!summaryCache) {
      setLoading(true);
    }
    setError(null);

    try {
      const latestYearResult = await query<{ latest_year: number }[]>(`
        SELECT MAX(year) AS latest_year
        FROM gold_kpi_muni_year
        WHERE municipality_id = :kommunId AND year <= :year
      `, { kommunId, year });

      const latestYearVal = latestYearResult[0]?.latest_year ?? year;
      const baseParams = {
        kommunId,
        year,
        latestYear: latestYearVal,
        startYear: year - 6,
        endYear: year,
        ...(huvudmanId ? { governance: huvudmanId } : {}),
        schoolId,
      };

      const studentCountKpis = (() => {
        if (huvudmanId === 'kommunal') return ['N11805', 'N15809'];
        if (huvudmanId === 'fristående') return ['N11806', 'N15810'];
        return ['N11805', 'N11806', 'N15809', 'N15810'];
      })();
      const studentKpiList = studentCountKpis.map(id => `'${id}'`).join(', ');

      const studentCountTable = huvudmanId ? 'gold_kpi_trend' : 'gold_kpi_muni_year';
      const studentCountValuePredicate = huvudmanId
        ? "entity_type = 'org_unit' AND gender = 'T' AND value IS NOT NULL AND value > 0"
        : 'value_t IS NOT NULL AND value_t > 0';
      const studentCountAtYearPredicate = huvudmanId
        ? "entity_type = 'org_unit' AND gender = 'T' AND year = :year AND value IS NOT NULL AND value > 0"
        : 'year = :year AND value_t IS NOT NULL AND value_t > 0';

      const [studentCountCoverageResult, distributionResult, aggregatedRows, driverResult, progressionResult, riksRows] = await Promise.all([
        query<
          {
            n_at_selected_year: number | null;
            bottleneck_year: number | null;
            max_latest_year: number | null;
            kpis_with_history: number | null;
          }[]
        >(`
        WITH per AS (
          SELECT kpi_id, MAX(year) AS latest_y
          FROM ${studentCountTable}
          WHERE municipality_id = :kommunId
            AND kpi_id IN (${studentKpiList})
            AND ${studentCountValuePredicate}
            AND year <= :year
          GROUP BY kpi_id
        ),
        at_sel AS (
          SELECT COUNT(DISTINCT kpi_id) AS n
          FROM ${studentCountTable}
          WHERE municipality_id = :kommunId
            AND kpi_id IN (${studentKpiList})
            AND ${studentCountAtYearPredicate}
        )
        SELECT
          (SELECT n FROM at_sel) AS n_at_selected_year,
          (SELECT MIN(latest_y) FROM per) AS bottleneck_year,
          (SELECT MAX(latest_y) FROM per) AS max_latest_year,
          (SELECT COUNT(*) FROM per) AS kpis_with_history
        `, { kommunId, year }),
        query<SchoolDistributionData[]>(`
          WITH KommunMedel AS (
            SELECT ${huvudmanId
            ? `CASE
                     WHEN SUM(CASE WHEN students > 0 THEN students ELSE 0 END) > 0
                       THEN SUM(value * students) / NULLIF(SUM(students), 0)
                     ELSE AVG(value)
                   END`
            : 'AVG(value_t)'
          } AS mu
            FROM ${huvudmanId ? 'gold_kpi_trend' : 'gold_kpi_muni_year'}
            WHERE municipality_id = :kommunId
              AND kpi_id = 'N15504'
              AND year = :latestYear
              ${huvudmanId ? "AND governance_type_year = :governance AND entity_type = 'org_unit' AND gender = 'T' AND value IS NOT NULL" : 'AND value_t IS NOT NULL'}
          ),
          SchoolValues AS (
            SELECT t.entity_id AS ou_id, t.entity_name AS ou_name, AVG(t.value) AS value
            FROM gold_kpi_trend t
            WHERE t.municipality_id = :kommunId AND t.kpi_id = 'N15504' AND t.gender = 'T'
              AND t.entity_type = 'org_unit' AND t.year = :latestYear
              ${huvudmanId ? 'AND t.governance_type_year = :governance' : ''}
              AND (:schoolId IS NULL OR t.entity_id = :schoolId)
              AND t.value IS NOT NULL
            GROUP BY t.entity_id, t.entity_name
          ),
          Params AS (SELECT mu, CASE WHEN 'N15504' LIKE 'N15%' THEN 3.0 ELSE 5.0 END AS tol FROM KommunMedel)
          SELECT s.ou_id, s.ou_name, s.value, s.value - p.mu AS diff_mot_kommunmedel, p.mu AS kommunMedel,
            CASE WHEN s.value >= p.mu + p.tol THEN 'over' WHEN s.value <= p.mu - p.tol THEN 'under' ELSE 'inline' END AS color_class
          FROM SchoolValues s CROSS JOIN Params p ORDER BY s.value DESC
        `, baseParams),
        query<any[]>(`
          WITH base AS (
            SELECT
              kpi_id,
              year,
              ${useMuniTable ? 'AVG(value_t)' : `
                CASE
                  WHEN SUM(CASE WHEN students > 0 THEN students ELSE 0 END) > 0
                    THEN SUM(value * students) / NULLIF(SUM(students), 0)
                  ELSE AVG(value)
                END`} AS value
            FROM ${useMuniTable ? 'gold_kpi_muni_year' : 'gold_kpi_trend'}
            WHERE municipality_id = :kommunId AND kpi_id IN (${kpiList})
              AND year BETWEEN :startYear AND :endYear
              ${useMuniTable ? 'AND value_t IS NOT NULL' : `
                AND entity_type = 'org_unit' AND gender = 'T'
                AND value IS NOT NULL
                ${huvudmanId ? 'AND governance_type_year = :governance' : ''}
                AND (:schoolId IS NULL OR entity_id = :schoolId)`}
            GROUP BY kpi_id, year
          )
          SELECT kpi_id, year, value, LAG(value) OVER (PARTITION BY kpi_id ORDER BY year) AS prev_year_value
          FROM base ORDER BY kpi_id, year
        `, baseParams),
        query<DriverData[]>(`
          WITH SalsaData AS (
            SELECT t.year, 
              CASE 
                WHEN SUM(CASE WHEN t.students > 0 THEN t.students ELSE 0 END) > 0
                  THEN SUM(t.value * t.students) / NULLIF(SUM(t.students), 0)
                ELSE AVG(t.value)
              END as salsa_avvikelse
            FROM gold_kpi_trend t
            WHERE t.municipality_id = :kommunId AND t.kpi_id = 'U15416' AND t.entity_type = 'org_unit' AND t.gender = 'T'
              AND t.year >= :year - 6 AND t.year <= :year AND (t.status IS NULL OR t.status <> 'Missing')
              AND t.value IS NOT NULL
            GROUP BY t.year
          ),
          LararData AS (
            SELECT year, AVG(value) as lärarbehörighet
            FROM gold_kpi_trend
            WHERE municipality_id = :kommunId AND kpi_id = 'N15813'
              AND entity_type = 'org_unit' AND gender = 'T'
              AND year >= :year - 6 AND year <= :year
              AND value IS NOT NULL
            GROUP BY year
          ),
          PersonalData AS (
            SELECT year, AVG(value) as personaltäthet
            FROM gold_kpi_trend
            WHERE municipality_id = :kommunId AND kpi_id = 'N15033'
              AND entity_type = 'org_unit' AND gender = 'T'
              AND year >= :year - 6 AND year <= :year
              AND value IS NOT NULL
            GROUP BY year
          ),
          EleverData AS (
            SELECT year, AVG(value) as elever_per_lärare
            FROM gold_kpi_trend
            WHERE municipality_id = :kommunId AND kpi_id = 'N15418'
              AND entity_type = 'org_unit' AND gender = 'T'
              AND year >= :year - 6 AND year <= :year
              AND value IS NOT NULL
            GROUP BY year
          ),
          AllYears AS (
            SELECT DISTINCT year FROM (
              SELECT year FROM SalsaData
              UNION ALL SELECT year FROM LararData
              UNION ALL SELECT year FROM PersonalData
              UNION ALL SELECT year FROM EleverData
            )
          )
          SELECT ay.year, COALESCE(s.salsa_avvikelse, NULL) as salsa_avvikelse, COALESCE(l.lärarbehörighet, NULL) as lärarbehörighet,
            COALESCE(p.personaltäthet, NULL) as personaltäthet, COALESCE(e.elever_per_lärare, NULL) as elever_per_lärare
          FROM AllYears ay 
          LEFT JOIN SalsaData s ON ay.year = s.year 
          LEFT JOIN LararData l ON ay.year = l.year
          LEFT JOIN PersonalData p ON ay.year = p.year 
          LEFT JOIN EleverData e ON ay.year = e.year 
          ORDER BY ay.year
        `, { kommunId, year }),
        query<ProgressionData[]>(`
          WITH a3 AS (SELECT municipality_id, year AS cohort_start_year, AVG(value_t) AS np3_andel FROM gold_kpi_muni_year WHERE kpi_id IN ('N15446', 'N15450') GROUP BY municipality_id, year),
          a6 AS (SELECT municipality_id, year, AVG(value_t) AS e6_andel FROM gold_kpi_muni_year WHERE kpi_id IN ('N15482', 'N15488', 'N15516', 'N15485') GROUP BY municipality_id, year),
          a9 AS (SELECT municipality_id, year, AVG(value_t) AS e9_andel FROM gold_kpi_muni_year WHERE kpi_id = 'N15418' GROUP BY municipality_id, year)
          SELECT a3.cohort_start_year, a3.np3_andel, a6.e6_andel, a9.e9_andel, (a6.e6_andel - a3.np3_andel) as tapp_3_6, (a9.e9_andel - a6.e6_andel) as tapp_6_9
          FROM a3 LEFT JOIN a6 ON a3.municipality_id = a6.municipality_id AND a6.year = a3.cohort_start_year + 3
          LEFT JOIN a9 ON a3.municipality_id = a9.municipality_id AND a9.year = a3.cohort_start_year + 6
          WHERE a3.municipality_id = :kommunId AND a3.cohort_start_year + 6 <= :year ORDER BY a3.cohort_start_year ASC
        `, { kommunId, year }),
        query<any[]>(`
          SELECT kpi_id, year, AVG(value_t) AS value
          FROM gold_kpi_muni_year
          WHERE kpi_id IN (${kpiList})
            AND year BETWEEN :startYear AND :endYear
            AND value_t IS NOT NULL
          GROUP BY kpi_id, year
        `, baseParams)
      ]);

      const cov = studentCountCoverageResult[0];
      const expectedStudentKpis = studentCountKpis.length;
      const nAtSelected = Number(cov?.n_at_selected_year ?? 0);
      const kpisWithHistory = Number(cov?.kpis_with_history ?? 0);
      const bottleneckYear = cov?.bottleneck_year != null ? Number(cov.bottleneck_year) : null;
      const maxLatestYear = cov?.max_latest_year != null ? Number(cov.max_latest_year) : null;

      // Require every student-count KPI to have positive data for the selected year (matches elevviktning inputs).
      // Banner only when some history exists but coverage is incomplete (carryForward explains the bottleneck year).
      const missingStudentCounts = kpisWithHistory > 0 && nAtSelected < expectedStudentKpis;

      setStudentCountStatus({
        selectedYear: year,
        latestAvailableYear: maxLatestYear,
        missingForSelectedYear: missingStudentCounts,
        carryForwardFromYear: missingStudentCounts && bottleneckYear != null ? bottleneckYear : null,
      });

      const processed = processKpiData(aggregatedRows, riksRows);
      const latestMerit = processed['N15504']?.find(entry => entry.year === latestYearVal)?.value ?? null;
      const latestSalsa = processed['U15416']?.find(entry => entry.year === latestYearVal)?.value ?? null;

      const meritSeries = processed['N15504'] ?? [];
      const salsaSeries = processed['U15416'] ?? [];
      const salsaByYear = new Map(salsaSeries.map(item => [item.year, item.value]));
      const trendResult: KommunTrendData[] = meritSeries.map(item => ({
        year: item.year,
        meritvarde: item.value,
        salsa_forvantat: salsaByYear.has(item.year) ? Number((item.value ?? 0) - (salsaByYear.get(item.year) ?? 0)) : null,
        riksValue: item.riksValue,
      }));

      const validatedProgression = progressionResult
        .filter((item: any) => item && item.cohort_start_year && !isNaN(item.cohort_start_year))
        .map((item: any) => ({
          ...item,
          np3_andel: item.np3_andel !== null && !isNaN(item.np3_andel) ? Number(item.np3_andel) : null,
          e6_andel: item.e6_andel !== null && !isNaN(item.e6_andel) ? Number(item.e6_andel) : null,
          e9_andel: item.e9_andel !== null && !isNaN(item.e9_andel) ? Number(item.e9_andel) : null,
          tapp_3_6: item.tapp_3_6 !== null && !isNaN(item.tapp_3_6) ? Number(item.tapp_3_6) : null,
          tapp_6_9: item.tapp_6_9 !== null && !isNaN(item.tapp_6_9) ? Number(item.tapp_6_9) : null
        }));

      // Calculate insights
      let insightResult: InsightData | null = null;
      if (trendResult.length >= 3 && driverResult.length >= 3) {
        const latestAvvikelse = latestSalsa ?? 0;
        const recentTrend = trendResult.slice(-3);
        const y = recentTrend.map(d => d.salsa_forvantat !== null ? d.meritvarde - d.salsa_forvantat : null).filter((val): val is number => val !== null);
        if (y.length >= 2) {
          const xFiltered = Array.from({ length: y.length }, (_, i) => i);
          const n = xFiltered.length;
          const sumX = xFiltered.reduce((a, b) => a + b, 0);
          const sumY = y.reduce((a, b) => a + b, 0);
          const sumXY = xFiltered.reduce((sum, xi, i) => sum + xi * y[i], 0);
          const sumXX = xFiltered.reduce((sum, xi) => sum + xi * xi, 0);
          const trendSlope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
          const recentDriver = driverResult.slice(-3);
          const salsaChanges = recentDriver.map((d, i) => i > 0 ? (d.salsa_avvikelse ?? 0) - (recentDriver[i - 1].salsa_avvikelse ?? 0) : 0).slice(1);
          const factors = [
            { name: 'lärarbehörighet', values: recentDriver.map(d => d.lärarbehörighet) },
            { name: 'personaltäthet', values: recentDriver.map(d => d.personaltäthet) },
            { name: 'elever_per_lärare', values: recentDriver.map(d => d.elever_per_lärare) }
          ];
          let strongestFactor = 'lärarbehörighet';
          let factorCorrelation = 0;
          factors.forEach(({ name, values }) => {
            const deltas = values.map((v, i) => i > 0 && v != null && values[i - 1] != null ? v - values[i - 1]! : 0).slice(1);
            if (deltas.length === salsaChanges.length && deltas.length > 0) {
              const corr = calculateCorrelation(salsaChanges, deltas);
              if (Math.abs(corr) > Math.abs(factorCorrelation)) { strongestFactor = name; factorCorrelation = corr; }
            }
          });
          insightResult = { latestAvvikelse, trendSlope, strongestFactor, factorCorrelation };
        }
      }

      setDistributionData(distributionResult);
      setAllKpiData(processed);
      setKpiData({
        avgMeritvarde: latestMerit,
        avgSalsaAvvikelse: latestSalsa,
        ranking: { rank: 1, total: 1 },
        spridning: null,
        reportedSchools: distributionResult.length,
        totalSchools: distributionResult.length,
        latestYear: latestYearVal,
      });
      latestYearForDistributionRef.current = latestYearVal;
      const kpiForDynamic = activeKpiRef.current;
      if (kpiForDynamic) void fetchDynamicDataRef.current?.(kpiForDynamic);

      setTrendData(trendResult);
      setDriverData(driverResult);
      setProgressionData(validatedProgression);
      if (insightResult) setInsightData(insightResult);

    } catch (err) {
      console.error('Failed to fetch kommun data:', err);
      setError(err instanceof Error ? err.message : 'Okänt fel');
    } finally {
      setLoading(false);
    }
  }, [kommunId, year, query, huvudmanId, skolaId, getCached, processKpiData]);

  const fetchDynamicData = useCallback(async (kpiId: string) => {
    if (!kommunId || !kpiId) return;
    const normalizedSchoolId = skolaId && skolaId !== 'null' ? skolaId : null;
    const cacheKey = `v${dataVersion}-${kommunId}-${kpiId}-${year}-${huvudmanId || 'all'}-${normalizedSchoolId || 'all'}-${analyticalFiltersKey}`;
    const cached = dynamicCacheRef.current.get(cacheKey);
    if (cached && (cached.trend?.length || cached.distribution?.length)) {
      setDynamicTrendData(cached.trend);
      setDynamicDistributionData(cached.distribution);
      return;
    }

    try {
      const latestYearForDistribution = latestYearForDistributionRef.current;

      // Build analytical filter SQL if needed
      const hasAnalytical = hasActiveAnalyticalFilters(analyticalFilters);
      const analyticalCTEs = hasAnalytical ? buildAnalyticalCTEs(analyticalFilters, {
        kommunId: kommunId || '',
        year: latestYearForDistribution,
        kpiId,
        governance: huvudmanId || undefined
      }) : '';
      const analyticalWhere = hasAnalytical ? buildAnalyticalWhere(analyticalFilters, {
        valueColumn: 's.value',
        salsaColumn: 'salsa_exp.salsa_expected',
        prevYearColumn: 'prev_yr.prev_value'
      }) : '';

      const [dynamicTrendResult, dynamicDistributionResult, genderResult] = await Promise.all([
        query<any[]>(`
          WITH SalsaDev AS (
            SELECT t.year,
              CASE
                WHEN SUM(CASE WHEN t.students > 0 THEN t.students ELSE 0 END) > 0
                  THEN SUM(t.value * t.students) / NULLIF(SUM(t.students), 0)
                ELSE AVG(t.value)
              END as salsa_avvikelse
            FROM gold_kpi_trend t
            WHERE t.municipality_id = :kommunId
              AND t.kpi_id = 'U15416'
              AND t.entity_type = 'org_unit'
              AND t.gender = 'T'
              AND t.year >= :year - 6 AND t.year <= :year
              AND (t.status IS NULL OR t.status <> 'Missing')
              AND t.value IS NOT NULL
            GROUP BY t.year
          ),
          RiksMuni AS (
            SELECT year, AVG(value_t) as riksValue
            FROM gold_kpi_muni_year
            WHERE kpi_id = :kpiId
              AND year BETWEEN :year - 6 AND :year
              AND value_t IS NOT NULL
            GROUP BY year
          ),
          RiksOu AS (
            SELECT
              year,
              CASE
                WHEN SUM(CASE WHEN students > 0 THEN students ELSE 0 END) > 0
                  THEN SUM(value * students) / NULLIF(SUM(students), 0)
                ELSE AVG(value)
              END AS riksValue
            FROM gold_kpi_trend
            WHERE kpi_id = :kpiId
              AND entity_type = 'org_unit'
              AND gender = 'T'
              AND year BETWEEN :year - 6 AND :year
              AND value IS NOT NULL
            GROUP BY year
          ),
          Riks AS (
            SELECT
              COALESCE(m.year, o.year) AS year,
              COALESCE(m.riksValue, o.riksValue) AS riksValue
            FROM RiksMuni m
            FULL OUTER JOIN RiksOu o
              ON o.year = m.year
          ),
          KommunKpi AS (
            SELECT t.year,
              ${huvudmanId || normalizedSchoolId
            ? 'CASE WHEN SUM(CASE WHEN t.kpi_id = :kpiId AND t.students > 0 THEN t.students ELSE 0 END) > 0 THEN SUM(CASE WHEN t.kpi_id = :kpiId THEN t.value * t.students ELSE 0 END) / NULLIF(SUM(CASE WHEN t.kpi_id = :kpiId THEN t.students ELSE 0 END), 0) ELSE AVG(CASE WHEN t.kpi_id = :kpiId THEN t.value ELSE NULL END) END'
            : 'AVG(CASE WHEN t.kpi_id = :kpiId THEN t.value_t ELSE NULL END)'} AS kpiValue,
              ${huvudmanId || normalizedSchoolId ? "CASE WHEN SUM(CASE WHEN t.kpi_id = 'N15504' AND t.students > 0 THEN t.students ELSE 0 END) > 0 THEN SUM(CASE WHEN t.kpi_id = 'N15504' THEN t.value * t.students ELSE 0 END) / NULLIF(SUM(CASE WHEN t.kpi_id = 'N15504' THEN t.students ELSE 0 END), 0) ELSE AVG(CASE WHEN t.kpi_id = 'N15504' THEN t.value ELSE NULL END) END" : "AVG(CASE WHEN t.kpi_id = 'N15504' THEN t.value_t ELSE NULL END)"} AS merit_muni
            FROM ${huvudmanId || normalizedSchoolId ? 'gold_kpi_trend' : 'gold_kpi_muni_year'} t
            WHERE t.municipality_id = :kommunId
              AND t.year BETWEEN :year - 6 AND :year
              ${huvudmanId || normalizedSchoolId ? "AND t.entity_type = 'org_unit' AND t.gender = 'T' AND t.value IS NOT NULL" : "AND t.value_t IS NOT NULL"}
              ${huvudmanId ? 'AND t.governance_type_year = :governance' : ''}
              ${normalizedSchoolId ? 'AND t.entity_id = :schoolId' : ''}
            GROUP BY t.year
          ),
          RiksMerit AS (SELECT year, AVG(value_t) AS riks_merit FROM gold_kpi_muni_year WHERE kpi_id = 'N15504' AND year BETWEEN :year - 6 AND :year AND value_t IS NOT NULL GROUP BY year),
          RiksSalsaDev AS (SELECT year, AVG(value_t) AS riks_salsa_avvikelse FROM gold_kpi_muni_year WHERE kpi_id = 'U15416' AND year BETWEEN :year - 6 AND :year AND value_t IS NOT NULL GROUP BY year),
          RiksExpectedMerit AS (SELECT m.year, (m.riks_merit - sd.riks_salsa_avvikelse) AS riks_expected_merit FROM RiksMerit m LEFT JOIN RiksSalsaDev sd ON sd.year = m.year)
          SELECT k.year, k.kpiValue, r.riksValue,
            CASE
              WHEN :kpiId = 'N15504' AND sd.salsa_avvikelse IS NOT NULL AND k.merit_muni IS NOT NULL
                THEN k.merit_muni - sd.salsa_avvikelse
              WHEN :kpiId <> 'N15504' AND sd.salsa_avvikelse IS NOT NULL AND k.merit_muni IS NOT NULL AND rem.riks_expected_merit IS NOT NULL AND rem.riks_expected_merit > 100
                THEN r.riksValue * ((k.merit_muni - sd.salsa_avvikelse) / rem.riks_expected_merit)
              ELSE r.riksValue
            END AS salsa_forvantat
          FROM KommunKpi k
          LEFT JOIN Riks r ON r.year = k.year
          LEFT JOIN SalsaDev sd ON sd.year = k.year
          LEFT JOIN RiksExpectedMerit rem ON rem.year = k.year
          ORDER BY k.year
        `, { kommunId, kpiId, year, governance: huvudmanId || null, schoolId: normalizedSchoolId }),
        query<DynamicDistributionData[]>(`
          WITH ${analyticalCTEs}
          KommunMedel AS (
            SELECT CASE WHEN SUM(CASE WHEN students > 0 THEN students ELSE 0 END) > 0 THEN SUM(value * students) / NULLIF(SUM(students), 0) ELSE AVG(value) END AS mu
            FROM gold_kpi_trend
            WHERE municipality_id = :kommunId AND kpi_id = :kpiId AND entity_type = 'org_unit' AND gender = 'T' AND year = :latestYear AND value IS NOT NULL
              ${huvudmanId ? 'AND governance_type_year = :governance' : ''}
          ),
          SchoolValues AS (
            SELECT t.entity_id AS ou_id, t.entity_name AS ou_name, AVG(t.value) AS value
            FROM gold_kpi_trend t
            WHERE t.municipality_id = :kommunId AND t.kpi_id = :kpiId AND t.gender = 'T' AND t.entity_type = 'org_unit' AND t.year = :latestYear AND t.value IS NOT NULL
              ${huvudmanId ? 'AND t.governance_type_year = :governance' : ''}
            GROUP BY t.entity_id, t.entity_name
          ),
          ${hasAnalytical && (analyticalFilters?.underExpected || analyticalFilters?.overExpected) ? `
          SalsaExpectedValues AS (
            SELECT
              entity_id AS ou_id,
              actual_value - salsa_expected AS salsa_expected
            FROM SalsaExpected
          ),
          ` : ''}
          ${hasAnalytical && analyticalFilters?.outliers ? `
          IQRBoundsCalc AS (
            SELECT q1, q3, iqr FROM IQRBounds
          ),
          ` : ''}
          ${hasAnalytical && analyticalFilters?.topImprovement ? `
          PrevYearValues AS (
            SELECT t.entity_id AS ou_id, AVG(t.value) AS prev_value
            FROM gold_kpi_trend t
            WHERE t.municipality_id = :kommunId AND t.kpi_id = :kpiId AND t.gender = 'T'
              AND t.entity_type = 'org_unit' AND t.year = :latestYear - 1 AND t.value IS NOT NULL
              ${huvudmanId ? 'AND t.governance_type_year = :governance' : ''}
            GROUP BY t.entity_id
          ),
          ImprovementThresholdCalc AS (
            SELECT p75_delta FROM ImprovementThreshold
          ),
          ` : ''}
          Params AS (SELECT mu, CASE WHEN :kpiId LIKE 'N15%' THEN 3.0 ELSE 5.0 END AS tol FROM KommunMedel)
          SELECT s.ou_id, s.ou_name, s.value, p.mu AS kommunMedel,
            CASE WHEN s.value >= p.mu + p.tol THEN 'over' WHEN s.value <= p.mu - p.tol THEN 'under' ELSE 'inline' END AS color_class
          FROM SchoolValues s
          CROSS JOIN Params p
          ${hasAnalytical && (analyticalFilters?.underExpected || analyticalFilters?.overExpected) ? 'LEFT JOIN SalsaExpectedValues salsa_exp ON s.ou_id = salsa_exp.ou_id' : ''}
          ${hasAnalytical && analyticalFilters?.outliers ? 'CROSS JOIN IQRBoundsCalc iqr_bounds' : ''}
          ${hasAnalytical && analyticalFilters?.topImprovement ? 'LEFT JOIN PrevYearValues prev_yr ON s.ou_id = prev_yr.ou_id CROSS JOIN ImprovementThresholdCalc improvement_threshold' : ''}
          WHERE 1=1 ${analyticalWhere}
          ORDER BY s.value DESC
        `, { kommunId, kpiId, latestYear: latestYearForDistribution, governance: huvudmanId, schoolId: normalizedSchoolId }),
        query<any[]>(`
          SELECT year,
            CASE WHEN SUM(CASE WHEN gender = 'K' AND students > 0 THEN students ELSE 0 END) > 0 THEN SUM(CASE WHEN gender = 'K' THEN value * students ELSE 0 END) / NULLIF(SUM(CASE WHEN gender = 'K' THEN students ELSE 0 END), 0) ELSE AVG(CASE WHEN gender = 'K' THEN value ELSE NULL END) END AS muni_girls,
            CASE WHEN SUM(CASE WHEN gender = 'M' AND students > 0 THEN students ELSE 0 END) > 0 THEN SUM(CASE WHEN gender = 'M' THEN value * students ELSE 0 END) / NULLIF(SUM(CASE WHEN gender = 'M' THEN students ELSE 0 END), 0) ELSE AVG(CASE WHEN gender = 'M' THEN value ELSE NULL END) END AS muni_boys
          FROM gold_kpi_trend
          WHERE municipality_id = :kommunId AND kpi_id = :kpiId AND entity_type = 'org_unit' AND year BETWEEN :year - 1 AND :year AND value IS NOT NULL
            ${huvudmanId ? 'AND governance_type_year = :governance' : ''}
          GROUP BY year ORDER BY year DESC
        `, { kommunId, kpiId, year, governance: huvudmanId })
      ]);

      const validTrend = dynamicTrendResult
        .filter(d => (getProp(d, "kpiValue") != null || getProp(d, "value") != null) && getProp(d, "year") != null)
        .map(d => ({
          year: Number(getProp(d, "year")),
          kpiValue: Number(getProp(d, "kpiValue") ?? getProp(d, "value")),
          salsa_forvantat: getProp(d, "salsa_forvantat") != null ? Number(getProp(d, "salsa_forvantat")) : null,
          riksValue: getProp(d, "riksValue") != null ? Number(getProp(d, "riksValue")) : null
        }));

      const validDist = dynamicDistributionResult
        .filter((d: any) => Number.isFinite(Number(d.value)) && Number.isFinite(Number(d.kommunMedel)))
        .map((d: any) => ({ ...d, value: Number(d.value), kommunMedel: Number(d.kommunMedel), color_class: d.color_class as 'over' | 'inline' | 'under' }));

      setDynamicTrendData(validTrend);
      setDynamicDistributionData(validDist);
      if (genderResult && genderResult.length > 0) {
        setGenderData(genderResult.map(r => ({ year: Number(r.year), muni_girls: r.muni_girls != null ? Number(r.muni_girls) : null, muni_boys: r.muni_boys != null ? Number(r.muni_boys) : null })));
      } else {
        setGenderData([]);
      }
      // Only cache non-empty payloads to avoid freezing "early empty" results
      if (validTrend.length > 0 || validDist.length > 0) {
        addToCache(dynamicCacheRef.current, cacheKey, { trend: validTrend, distribution: validDist });
      }
    } catch (err) {
      console.error('Failed to fetch dynamic data:', err);
    }
  }, [kommunId, year, query, huvudmanId, skolaId, addToCache, analyticalFilters, analyticalFiltersKey, dataVersion]);

  fetchDynamicDataRef.current = fetchDynamicData;

  const fetchCorrelationData = useCallback(async (kpiId: string) => {
    if (!kommunId || !kpiId) return;
    const cacheKey = `v${dataVersion}-${kommunId}-${kpiId}-${year}-${huvudmanId || 'all'}`;
    if (correlationDebounceRef.current) clearTimeout(correlationDebounceRef.current);
    const cached = correlationCacheRef.current.get(cacheKey);
    const cachedHasAny =
      cached &&
      (Object.values(cached.correlation || {}).some((arr: any) => Array.isArray(arr) && arr.length > 0) ||
        Object.values(cached.riket || {}).some((arr: any) => Array.isArray(arr) && arr.length > 0));
    if (cachedHasAny) {
      setCorrelationData(cached.correlation);
      setRiketData(cached.riket);
      return;
    }

    correlationDebounceRef.current = setTimeout(async () => {
      try {
        const allCorr = await query<any[]>(`
        WITH PairedData AS (
          SELECT m.year, m.entity_id AS ou_id, m.entity_name AS ou_name, m.value AS kpiValue, d.value AS driverValue, d.kpi_id AS driverKpiId
          FROM gold_kpi_trend AS m JOIN gold_kpi_trend AS d ON m.entity_id = d.entity_id AND m.year = d.year
          WHERE m.municipality_id = :kommunId AND m.kpi_id = :kpiId AND d.kpi_id IN ('N15033', 'N15813')
            AND m.entity_type = 'org_unit' AND d.entity_type = 'org_unit' AND m.gender = 'T' AND d.gender = 'T'
            AND m.value IS NOT NULL AND d.value IS NOT NULL AND m.year >= :startYear AND m.year <= :year
            ${huvudmanId ? 'AND m.governance_type_year = :governance AND d.governance_type_year = :governance' : ''}
        ),
        PairedBySchool AS (
          SELECT year, ou_id, any_value(ou_name) AS ou_name, driverKpiId, avg(kpiValue) AS kpiValue, avg(driverValue) AS driverValue
          FROM PairedData GROUP BY year, ou_id, driverKpiId
        ),
        RiketData AS (
          SELECT m.year, m.value_t AS kpiValue, d.value_t AS driverValue, d.kpi_id AS driverKpiId
          FROM gold_kpi_muni_year AS m JOIN gold_kpi_muni_year AS d ON m.municipality_id = d.municipality_id AND m.year = d.year
          WHERE m.kpi_id = :kpiId AND d.kpi_id IN ('N15033', 'N15813')
            AND m.value_t IS NOT NULL AND d.value_t IS NOT NULL AND m.year >= :startYear AND m.year <= :year
        )
        SELECT 'kommun' as data_type, year, ou_id, ou_name, kpiValue, driverValue, driverKpiId FROM PairedBySchool
        UNION ALL
        SELECT 'riket' as data_type, year, NULL as ou_id, NULL as ou_name, kpiValue, driverValue, driverKpiId FROM RiketData
        ORDER BY driverKpiId, data_type, year DESC
      `, { kommunId, kpiId, startYear: year - 5, year, governance: huvudmanId });

        const corrRes: { [driver: string]: CorrelationYearSummary[] } = {};
        const riketRes: { [driver: string]: { year: number, r: number }[] } = {};
        const byDriver = allCorr.reduce((acc: any, row: any) => {
          const dId = row.driverKpiId;
          if (!acc[dId]) acc[dId] = { kommun: [], riket: [] };
          if (row.data_type === 'kommun') acc[dId].kommun.push(row); else acc[dId].riket.push(row);
          return acc;
        }, {});

        for (const [dKpiId, dData] of Object.entries(byDriver)) {
          const validKommun = (dData as any).kommun.filter((d: any) => !isNaN(Number(d.kpiValue)) && !isNaN(Number(d.driverValue))).map((d: any) => ({ year: Number(d.year), ou_id: d.ou_id, ou_name: d.ou_name, kpiValue: Number(d.kpiValue), driverValue: Number(d.driverValue) }));
          const validRiket = (dData as any).riket.filter((d: any) => !isNaN(Number(d.kpiValue)) && !isNaN(Number(d.driverValue))).map((d: any) => ({ year: Number(d.year), kpiValue: Number(d.kpiValue), driverValue: Number(d.driverValue) }));
          const groupedByYear = validKommun.reduce((acc: any, p: any) => { if (!acc[p.year]) acc[p.year] = []; acc[p.year].push(p); return acc; }, {});
          const riketByYear = validRiket.reduce((acc: any, p: any) => { if (!acc[p.year]) acc[p.year] = []; acc[p.year].push(p); return acc; }, {});
          riketRes[dKpiId] = Object.entries(riketByYear).map(([yS, pts]: [string, any]) => ({ year: Number(yS), r: calcCorrelation(pts.map((p: any) => ({ x: p.driverValue, y: p.kpiValue }))) })).sort((a, b) => a.year - b.year);
          corrRes[dKpiId] = Object.entries(groupedByYear).map(([yS, pts]: [string, any]) => {
            const y = Number(yS); const n = pts.length;
            const cD = pts.map((p: any) => ({ x: p.driverValue, y: p.kpiValue }));
            return {
              year: y, r: calcCorrelation(cD), n,
              avgDriver: pts.reduce((s: any, p: any) => s + p.driverValue, 0) / n,
              avgKpi: pts.reduce((s: any, p: any) => s + p.kpiValue, 0) / n,
              minDriver: Math.min(...pts.map((p: any) => p.driverValue)),
              maxDriver: Math.max(...pts.map((p: any) => p.driverValue)),
              minKpi: Math.min(...pts.map((p: any) => p.kpiValue)),
              maxKpi: Math.max(...pts.map((p: any) => p.kpiValue)),
              points: pts.map((p: any) => ({
                ou_id: p.ou_id,
                ou_name: p.ou_name,
                kpiValue: p.kpiValue,
                driverValue: p.driverValue,
                year: y
              }))
            };
          }).sort((a, b) => b.year - a.year);
        }
        const hasAny =
          Object.values(corrRes).some(arr => Array.isArray(arr) && arr.length > 0) ||
          Object.values(riketRes).some(arr => Array.isArray(arr) && arr.length > 0);
        if (hasAny) {
          addToCache(correlationCacheRef.current, cacheKey, { correlation: corrRes, riket: riketRes });
        }
        setCorrelationData(corrRes);
        setRiketData(riketRes);
      } catch (err) {
        console.error('Failed to fetch correlation data:', err);
        // If it looks like a memory error, we can try to clear some cache or just log it
        if (String(err).includes('could not allocate')) {
          console.warn('Memory allocation failed during correlation query. Large municipality?');
        }
      }
    }, 300);
  }, [kommunId, year, query, huvudmanId, addToCache, dataVersion]);

  // Re-run when DuckDB invalidates caches (fetchData body does not reference dataVersion; effect deps do).
  useEffect(() => {
    fetchData();
  }, [fetchData, dataVersion]);

  useEffect(() => {
    const timer = correlationDebounceRef.current;
    const abort = abortControllerRef.current;
    return () => {
      if (timer) clearTimeout(timer);
      if (abort) abort.abort();
    };
  }, []);

  useEffect(() => {
    dynamicCacheRef.current.clear();
    correlationCacheRef.current.clear();
    setDynamicTrendData([]);
    setDynamicDistributionData([]);
    setDistributionData([]);
    setCorrelationData({});
    setRiketData({});
    setLastCorrelationKey('');
  }, [kommunId, year, huvudmanId, dataVersion]);

  const correlationDataKey = `v${dataVersion}-${kommunId}-${activeKpi}-${year}-${huvudmanId || 'all'}`;

  useEffect(() => {
    if (activeKpi) {
      fetchDynamicData(activeKpi);
      if (correlationDataKey !== lastCorrelationKey) {
        setLastCorrelationKey(correlationDataKey);
        fetchCorrelationData(activeKpi);
      }
    }
  }, [activeKpi, huvudmanId, correlationDataKey, lastCorrelationKey, fetchDynamicData, fetchCorrelationData, dataVersion]);

  return {
    loading,
    error,
    kpiData,
    trendData,
    distributionData,
    driverData,
    insightData,
    allKpiData,
    dynamicTrendData,
    dynamicDistributionData,
    genderData,
    progressionData,
    correlationData,
    riketData,
    studentCountStatus
  };
};
